import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditMultiSelectField } from '../../../EditMultiSelectField';
import {
  GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES,
  type InvestmentPreferencesData
} from '../queries/accountPreferencesQuery';

const GET_ALL_PREFERRED_INVESTMENT_TYPES = gql`
  query GetAllPreferredInvestmentTypes {
    preferredInvestmentTypes {
      id
      name
      isActive
    }
  }
`;

interface AllPreferredInvestmentTypesData {
  preferredInvestmentTypes: PreferredInvestmentTypeItem[];
}

interface PreferredInvestmentTypeItem {
  id: string;
  isActive: boolean;
  name: string;
}

const UPDATE_ACCOUNT_INVESTMENT_TYPES = gql`
  mutation UpdateAccountInvestmentTypes($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        name
        preferredInvestmentTypesIds {
          id
          name
          isActive
        }
      }
    }
  }
`;

interface EditPreferredInvestmentTypesDialogProps {
  accountId: string;
  initialValue: InvestmentPreferencesData;
  isOpen: boolean;
  onChange: (data: InvestmentPreferencesData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: InvestmentPreferencesData;
}

export function EditPreferredInvestmentTypesDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditPreferredInvestmentTypesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Fetch all available investment types
  const { data: allTypesData, loading: loadingTypes } =
    useQuery<AllPreferredInvestmentTypesData>(
      GET_ALL_PREFERRED_INVESTMENT_TYPES,
      {
        fetchPolicy: 'cache-first'
      }
    );

  const [updateAccount] = useMutation(UPDATE_ACCOUNT_INVESTMENT_TYPES, {
    refetchQueries: [
      {
        query: GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES,
        variables: { accountId }
      }
    ]
  });

  // Convert API data to options for multi-select
  const options = useMemo((): { label: string; value: string }[] => {
    if (!allTypesData?.preferredInvestmentTypes) return [];
    return allTypesData.preferredInvestmentTypes
      .filter(item => item.isActive)
      .map(item => ({
        label: item.name,
        value: item.id
      }));
  }, [allTypesData]);

  // Get selected IDs from value
  const selectedIds = useMemo(
    (): string[] => value.preferredInvestmentTypes.map(t => t.id),
    [value.preferredInvestmentTypes]
  );

  // Check if dirty by comparing IDs
  const initialIds = useMemo(
    (): string[] => initialValue.preferredInvestmentTypes.map(t => t.id).sort(),
    [initialValue.preferredInvestmentTypes]
  );
  const currentIds = useMemo(
    (): string[] => [...selectedIds].sort(),
    [selectedIds]
  );
  const isDirty = JSON.stringify(currentIds) !== JSON.stringify(initialIds);

  const handleChange = (ids: string[]) => {
    // Convert IDs back to PreferenceItem objects
    const items = ids.map(id => {
      // Check if it's already in value
      const existing = value.preferredInvestmentTypes.find(t => t.id === id);
      if (existing) return existing;
      // Otherwise find from options
      const option = options.find(o => o.value === id);
      return { id, name: option?.label ?? '' };
    });
    onChange({ ...value, preferredInvestmentTypes: items });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: {
          id: accountId,
          values: {
            preferredInvestmentTypeIds: selectedIds
          }
        }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save investment types');
      } else {
        toast.success('Investment types saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving investment types:', error);
      toast.error('Failed to save investment types');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Preferred Investment Type"
    >
      <EditMultiSelectField
        label="Preferred Investment Types"
        onChange={handleChange}
        options={options}
        placeholder={loadingTypes ? 'Loading...' : 'Select investment types...'}
        values={selectedIds}
      />
    </EditDialog>
  );
}
